import { ComponentRef } from "react";
import { ReactRenderer } from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import { computePosition, flip, offset } from "@floating-ui/react";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";
import { PluginKey } from "@tiptap/pm/state";
import { Suggestion } from "@tiptap/suggestion";

import DefaultCompanyLogo from "@assets/icons/building-skyscraper.svg?url";
import LevelZeroIcon from "@assets/icons/sourcing-theme-level-zero.svg?inline";
import LevelOneIcon from "@assets/icons/sourcing-theme-level-one.svg?inline";
import LevelTwoIcon from "@assets/icons/sourcing-theme-level-two.svg?inline";
import LevelThreeIcon from "@assets/icons/sourcing-theme-level-three.svg?inline";

import { queryClient } from "@config/queryClient";
import { SOURCING_SETTINGS_QUERY_KEY } from "@config/query";
import {
  isAINote,
  isCompany,
  isDeal,
  isDocument,
  isFolder,
  isFund,
  isMeeting,
  isMember,
  isOpportunity,
  isPerson,
  isThemes,
  isThread,
} from "@utils/related-items.util";
import { getMeetingTypeData } from "@utils/meeting.util";
import { getUserInitials } from "@utils/users.util";
import { getRelativeTime } from "@utils/date.util";
import { normalizeLogoUrl } from "@utils/logo.util";
import { cn } from "@utils/tailwind.util";
import { createElement } from "../utils";
import { DESKTOP_BREAKPOINT } from "@hooks/useDeviceSize";
import type { RelatedItem } from "@app-types/related-items.type";
import type { MentionNodeAttrs, MentionOptions } from "./types";
import { MentionList } from "./MentionList";
import { isValidUrl } from "@utils/strings.util";
import { FundStatus } from "@app-types/common.type";

export const MentionPluginKey = new PluginKey("mention");

const icons = {
  // prettier-ignore
  deal: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 14 18"><path stroke="#090A0C" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M8.666 1.5v3.333a.833.833 0 0 0 .834.834h3.333M8.666 1.5H2.833a1.667 1.667 0 0 0-1.667 1.667v11.666A1.666 1.666 0 0 0 2.833 16.5h8.334a1.666 1.666 0 0 0 1.666-1.667V5.667M8.666 1.5l4.167 4.167m-4.167 2.5H6.583a1.25 1.25 0 1 0 0 2.5h.833a1.25 1.25 0 0 1 0 2.5H5.333m1.667 0V14m0-6.667v.834"/></svg>',
  // prettier-ignore
  meeting: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 19 19"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M8.83 16.5H3.166A1.667 1.667 0 0 1 1.5 14.833v-10a1.667 1.667 0 0 1 1.667-1.666h10a1.667 1.667 0 0 1 1.666 1.666v3.334H1.5m10-6.667v3.333M4.833 1.5v3.333M14 12.747V14l.833.833M10.667 14a3.333 3.333 0 1 0 6.666 0 3.333 3.333 0 0 0-6.666 0Z"/></svg>',
  // prettier-ignore
  document: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 14 18"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M8.666 1.5v3.333a.833.833 0 0 0 .834.834h3.333M8.666 1.5H2.833a1.667 1.667 0 0 0-1.667 1.667v11.666A1.666 1.666 0 0 0 2.833 16.5h8.334a1.666 1.666 0 0 0 1.666-1.667V5.667M8.666 1.5l4.167 4.167m-8.333 7.5h5m-5-3.334h5"/></svg>',
  // prettier-ignore
  memo: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 14 18"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M8.666 1.5v3.333a.833.833 0 0 0 .834.834h3.333M8.666 1.5H2.833a1.667 1.667 0 0 0-1.667 1.667v11.666A1.666 1.666 0 0 0 2.833 16.5h8.334a1.666 1.666 0 0 0 1.666-1.667V5.667M8.666 1.5l4.167 4.167M4.708 8.234v5.181M9.512 9.53a1.295 1.295 0 1 0-2.59 0v2.59a1.295 1.295 0 1 0 2.59 0"/></svg>',
  // prettier-ignore
  folder: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0 text-[#6B7080]"><path d="M9 3a1 1 0 0 1 .608 .206l.1 .087l2.706 2.707h6.586a3 3 0 0 1 2.995 2.824l.005 .176v8a3 3 0 0 1 -2.824 2.995l-.176 .005h-14a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-11a3 3 0 0 1 2.824 -2.995l.176 -.005h4z" fill="currentColor" stroke-width="0"></path></svg>',
  // prettier-ignore
  thread: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0 text-[#6B7080]"><path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"></path><path d="M3 7l9 6l9 -6"></path></svg>',
  // prettier-ignore
  note: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0"><path d="M13 20l7 -7"></path><path d="M13 20v-6a1 1 0 0 1 1 -1h6v-7a2 2 0 0 0 -2 -2h-12a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7"></path></svg>',
  // prettier-ignore
  company: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" class="flex-shrink-0"><path stroke="currentStroke" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M2.5 17.5h15m-13.333 0V5.833L10.833 2.5v15m5 0V9.167l-5-3.334M7.5 7.5v.008M7.5 10v.008m0 2.492v.008M7.5 15v.008"></path></svg>',
} as const;

class MentionRenderer {
  private options: MentionOptions;
  private node: ProsemirrorNode;
  private HTMLAttributes: Record<string, any>;

  constructor(options: MentionOptions, node: any, HTMLAttributes: Record<string, any>) {
    this.options = options;
    this.node = node;
    this.HTMLAttributes = HTMLAttributes;
  }

  renderHTML() {
    const attributes = this.getAttributes();
    const rootNode = createElement("span", attributes, (attributes.class as string) ?? "");
    const iconNode = this.createIconNode();
    const contentNode = this.createContentNode();

    rootNode.appendChild(iconNode);
    rootNode.appendChild(contentNode);

    // CSP-safe equivalent of inline onerror — strict CSPs (e.g. Chrome Extension MV3)
    // block inline handlers injected via innerHTML, so attach the listener here.
    iconNode.querySelectorAll("img").forEach((img) => {
      img.addEventListener("error", () => {
        img.style.display = "none";
      });
    });

    rootNode.title = this.getMentionTitle();
    rootNode.onclick = () => this.options.onClick(this.node.attrs as MentionNodeAttrs);

    return { dom: rootNode };
  }

  private getAttributes(): Record<string, any> {
    return mergeAttributes(this.options.HTMLAttributes, this.HTMLAttributes, {
      class: cn(
        "mention select-all cursor-pointer whitespace-nowrap inline-block rounded-lg text-sm font-medium py-0.5 px-1 border",
        "bg-background-primary border-stroke-primary text-content-primary",
        "dark:bg-gray-600 dark:hover:bg-gray-700 dark:border-gray-700 dark:hover:border-gray-600 dark:text-invertedPrimary80"
      ),
      style: `vertical-align: ${this.options.verticalAlign}; line-height: 0;`,
    });
  }

  private createIconNode() {
    const { is_memo, type, logo, object_type, stage_color } = this.node.attrs;

    let iconContent = "";

    // <div class="h-full w-full rounded-[5px] bg-cover bg-center flex items-center justify-center border border-gray-200 bg-background-clear flex-shrink-0" style="background-image: url(${logo});"></div>

    // TODO: drop normalizeLogoUrl once Scout Agent index is rebuilt with new BE URL format —
    // applied here defensively for legacy mentions saved with /fallback/transparent URLs.
    const normalizedLogo = typeof logo === "string" ? normalizeLogoUrl(logo) : logo;
    if (logo) {
      if (object_type === "companies.company") {
        iconContent = `<div class="relative flex shrink-0 items-center justify-center overflow-hidden rounded-[5px] bg-white dark:bg-transparent">
            <img src="${normalizedLogo}" class="absolute h-full w-full object-contain z-10" />
            ${icons.company}
          </div>`;
      } else if (object_type === "people.person" && isValidUrl(normalizedLogo as string)) {
        iconContent = `<div
            class="relative h-full w-full rounded-full flex shrink-0 items-center justify-center overflow-hidden bg-white dark:bg-transparent border border-gray-200 text-content-secondary">
            <img src="${normalizedLogo}" class="absolute h-full w-full object-contain z-10" />
            <span class="flex h-full w-full items-center justify-center bg-slate-100 text-[0.5rem]">
              ${this.node.attrs?.title[0]?.charAt(0)?.toUpperCase() || "-"}
            </span>
          </div>`;
      } else if (object_type === "accounts.user" && isValidUrl(normalizedLogo as string)) {
        iconContent = `<div class="relative h-full w-full rounded-full flex shrink-0 items-center justify-center overflow-hidden border border-gray-200 bg-background-clear text-content-secondary">
            <img src="${normalizedLogo}" class="absolute h-full w-full object-cover z-10" />
          </div>`;
      } else if (object_type === "sourcing.theme") {
        iconContent = logo;
      } else if (object_type === "emails.thread") {
        iconContent = icons["thread"];
      } else {
        iconContent = `<div class="overflow-hidden bg-white w-4 h-4 text-[8px] flex shrink-0 items-center justify-center rounded-full border"><span class="text-[#9CA1AF]">${logo}</span></div>`;
      }
    } else if (is_memo) {
      iconContent = icons["memo"];
    } else if (type === "fund") {
      const status = this.node.attrs.status as FundStatus | undefined;
      const variantClasses =
        status === "Closed" ? "border-[1.6px] border-content-secondary" : "bg-emerald-700";
      iconContent = `<span class="flex h-full w-full items-center justify-center"><span class="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full ${variantClasses}"></span></span>`;
    } else if (type === "opportunity") {
      iconContent = `<span class="flex h-full w-full items-center justify-center"><span class="inline-block h-2.5 w-2.5 flex-shrink-0 rotate-45 rounded-[1px]" style="background-color: ${stage_color};"></span></span>`;
    } else if (type in icons) {
      if (type === "deal") {
        iconContent = `<div class="w-3 h-3 rounded-[5px] m-0.5" style="background: ${stage_color};"></div>`;
      } else {
        iconContent = icons[type as keyof typeof icons];
      }
    }

    const iconNode = createElement(
      "span",
      { innerHTML: iconContent },
      "inline-block align-middle mr-0.5 h-4 w-4 flex-shrink-0 select-none leading-[0]"
    );

    return iconNode;
  }

  private createContentNode() {
    const contentNode = createElement(
      "span",
      {},
      "inline-block align-middle select-text leading-tight"
    );

    if (this.node.attrs.type === "meeting") {
      const meetingLineNode = this.createMeetingLineNode();
      contentNode.appendChild(meetingLineNode);
    }

    const titleNode = this.createTitleNode();
    contentNode.appendChild(titleNode);
    return contentNode;
  }

  private createMeetingLineNode() {
    const meetingLineNode = createElement(
      "span",
      {},
      "before:inline-block before:h-4 before:mr-1 before:align-middle before:w-[3px] before:rounded before:bg-current"
    );
    meetingLineNode.style.color = this.node.attrs.color;

    return meetingLineNode;
  }

  private createTitleNode() {
    const titleNode = createElement(
      "span",
      { textContent: this.node.attrs.title },
      "inline-block align-middle text-content-primary text-overflow-ellipsis max-w-[180px] dark:text-white"
    );
    return titleNode;
  }

  private getMentionTitle() {
    const attributes = this.node.attrs as MentionNodeAttrs;

    if (attributes.type === "deal") {
      return `${attributes.title} [${attributes.stage}]`;
    }

    if (attributes.type === "document") {
      const filename = attributes.title;
      const is_memo = attributes.is_memo ? "[IC]" : "";
      const deal = attributes.deal ? `in "${attributes.deal}" deal` : "";

      return [filename, is_memo, deal].filter(Boolean).join(" ");
    }

    if (attributes.type === "meeting") {
      const formattedTime = getRelativeTime(attributes.start_time);
      return `${attributes.title} • ${formattedTime}`;
    }

    return "";
  }
}

export const Mention = Node.create<MentionOptions>({
  name: "mention",

  addOptions() {
    return {
      HTMLAttributes: {},
      onClick: () => {},
      verticalAlign: "middle",
    };
  },

  atom: true,
  draggable: false,
  group: "inline",
  inline: true,
  selectable: true,

  addAttributes() {
    return {
      type: {
        default: "deal",
        parseHTML: (element) => element.getAttribute("data-type"),
        renderHTML: (attributes) => ({ "data-type": attributes.type }),
      },
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-id"),
        renderHTML: (attributes) => ({ "data-id": attributes.id }),
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-title"),
        renderHTML: (attributes) => ({ "data-title": attributes.title }),
      },
      color: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-color"),
        renderHTML: (attributes) =>
          attributes.type === "meeting" ? { "data-color": attributes.color } : {},
      },
      is_memo: {
        default: false,
        parseHTML: (element) => element.getAttribute("data-is-memo") === "true",
        renderHTML: (attributes) =>
          attributes.type === "document" ? { "data-is-memo": attributes.is_memo } : {},
      },
      deal: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-deal"),
        renderHTML: (attributes) =>
          attributes.type === "document" ? { "data-deal": attributes.deal } : {},
      },
      start_time: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-start-time"),
        renderHTML: (attributes) =>
          attributes.type === "meeting" ? { "data-start-time": attributes.start_time } : {},
      },
      stage: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-stage"),
        renderHTML: (attributes) =>
          attributes.type === "deal" || attributes.type === "opportunity"
            ? { "data-stage": attributes.stage }
            : {},
      },
      file: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-file"),
        renderHTML: (attributes) =>
          attributes.type === "document" ? { "data-file": attributes.file } : {},
      },
      logo: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-logo-url"),
        renderHTML: (attributes) =>
          attributes.type === "company" ? { "data-logo-url": attributes.logo } : {},
      },
      object_type: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-object-type"),
        renderHTML: (attributes) => ({ "data-object-type": attributes.object_type }),
      },
      stage_color: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-stage-color"),
        renderHTML: (attributes) => ({ "data-stage-color": attributes.stage_color }),
      },
      status: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-status"),
        renderHTML: (attributes) =>
          attributes.type === "fund" ? { "data-status": attributes.status } : {},
      },
      __original_item: { rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-type]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return new MentionRenderer(this.options, node, HTMLAttributes).renderHTML();
  },

  renderText({ node }) {
    return `(${node.attrs.type}:${node.attrs.id})`;
  },

  addCommands() {
    function getAttributes(options: MentionNodeAttrs | RelatedItem): MentionNodeAttrs {
      let nodeAttrs: MentionNodeAttrs | null = null;

      if (isDeal(options)) {
        nodeAttrs = {
          type: "deal",
          id: options.id,
          title: options.title,
          stage: options.stage?.title,
          stage_color: options.stage?.color ?? options.stage_color,
          object_type: options.object_type,
          __original_item: options,
        };
      }

      if (isDocument(options)) {
        nodeAttrs = {
          object_type: options.object_type,
          type: "document",
          id: options.id,
          title: options.name ?? options.file_name,
          is_memo: options.is_memo,
          deal: options.deal?.title,
          file: options.file,
          __original_item: options,
        };
      }

      if (isFolder(options)) {
        nodeAttrs = {
          object_type: options.object_type,
          type: "folder",
          id: options.id,
          title: options.name,
          __original_item: options,
        };
      }

      if (isThemes(options)) {
        const sourcingSettings = queryClient.getQueryData<{ zero_level_count?: number }>([
          SOURCING_SETTINGS_QUERY_KEY,
        ]);
        const isLevelOneBased = sourcingSettings?.zero_level_count === 1;
        const themeLevel = isLevelOneBased ? options.level - 1 : options.level;

        let logo = "";
        const levelIcons = [LevelZeroIcon, LevelOneIcon, LevelTwoIcon, LevelThreeIcon];
        const icon = levelIcons[Math.max(0, themeLevel)] ?? LevelThreeIcon;
        logo = `<img src="data:image/svg+xml,${encodeURIComponent(icon)}" alt="Level ${themeLevel}" class="h-4 w-4 !m-0" />`;

        nodeAttrs = {
          object_type: options.object_type,
          type: "theme",
          id: options.id,
          title: options.title,
          logo: logo,
          __original_item: options,
        };
      }

      if (isMeeting(options)) {
        nodeAttrs = {
          type: "meeting",
          id: options.id,
          title: options.title,
          color: getMeetingTypeData(options?.type)?.color ?? "#10B981",
          start_time: options.start_time,
          object_type: options.object_type,
          __original_item: options,
        };
      }

      if (isCompany(options)) {
        nodeAttrs = {
          type: "company",
          id: options.id,
          title: options.title,
          // TODO: drop normalizeLogoUrl once Scout Agent index is rebuilt with new BE URL format.
          logo: normalizeLogoUrl(options.logo_url ?? options.logo) || DefaultCompanyLogo,
          object_type: options.object_type,
          __original_item: options,
        };
      }

      if (isPerson(options)) {
        nodeAttrs = {
          type: "person",
          id: options.id,
          first_name: options.first_name,
          last_name: options.last_name,
          email: options.email,
          title:
            options.first_name && options.last_name
              ? `${options.first_name} ${options.last_name}`
              : options.email,
          // TODO: drop normalizeLogoUrl once Scout Agent index is rebuilt with new BE URL format.
          logo:
            options.logo && options.logo.length > 0
              ? normalizeLogoUrl(options.logo) ?? getUserInitials(options)
              : getUserInitials(options),
          object_type: options.object_type,
          __original_item: options,
        };
      }

      if (isMember(options)) {
        nodeAttrs = {
          type: "member",
          id: options.id,
          first_name: options.first_name,
          last_name: options.last_name,
          email: options.email,
          title:
            options.first_name && options.last_name
              ? `${options.first_name} ${options.last_name}`
              : options.email,
          logo:
            options.avatar_url && options.avatar_url.length > 0
              ? options.avatar_url
              : getUserInitials(options),
          object_type: options.object_type,
          __original_item: options,
        };
      }

      if (isThread(options)) {
        nodeAttrs = {
          type: "thread",
          id: options.id,
          title: options.subject,
          object_type: options.object_type,
          logo: icons["thread"],
          __original_item: options,
        };
      }

      if (isAINote(options)) {
        nodeAttrs = {
          type: "note",
          id: options.id,
          title: options.title,
          access: options.access,
          description: options.description,
          state: options.state,
          created: options.created,
          modified: options.modified,
          object_type: "notes.ainote",
          __original_item: options,
        };
      }

      if (isFund(options)) {
        nodeAttrs = {
          type: "fund",
          id: options.id,
          title: options.title,
          status: options.status,
          object_type: options.object_type,
          __original_item: options,
        };
      }

      if (isOpportunity(options)) {
        nodeAttrs = {
          type: "opportunity",
          id: options.id,
          title: options.title,
          stage: options.stage?.title,
          stage_color: options.stage?.color,
          object_type: options.object_type,
          __original_item: options,
        };
      }

      return nodeAttrs || (options as MentionNodeAttrs);
    }

    return {
      setMention:
        (options) =>
        ({ state, commands }) => {
          const { selection } = state;
          const { $from } = selection;
          const position = { from: $from.pos - 1, to: $from.pos };

          return commands.setMentionAt(position, options);
        },
      setMentionAt:
        (position, options) =>
        ({ chain, editor }) => {
          const nodeAttrs = getAttributes(options);

          if (!nodeAttrs) return false;

          editor.emit("mentionAdded", { item: options, attrs: nodeAttrs });

          const content = [
            { type: this.name, attrs: nodeAttrs },
            { type: "text", text: " " },
          ];

          return chain().insertContentAt(position, content).focus().run();
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion<RelatedItem>({
        editor: this.editor,
        char: "@",
        startOfLine: false,
        pluginKey: MentionPluginKey,
        command: ({ props, range, editor }) => {
          const { nodeAfter, nodeBefore } = editor.view.state.selection.$to;
          const overrideSpace = nodeAfter?.text?.startsWith(" ");

          // Move the cursor to the end of the text written after the @
          if (nodeBefore && nodeBefore.isText) {
            const singleCharRegex = /(^|\s)@(\s|$)/;
            const fullQuery = nodeBefore.textContent;

            const matches = fullQuery.match(/@\S+/gu);
            const onlySuggestionChar = singleCharRegex.test(fullQuery);
            let mentionQuery = "";
            const mentionPattern = /@.+$/;

            try {
              const rangedText = editor.state.doc.textBetween(range.from, range.to, " ");
              mentionQuery = rangedText;
            } catch {
              mentionQuery = matches?.[0] ?? "";
            }

            if (mentionPattern.test(mentionQuery) || onlySuggestionChar) {
              range = {
                from: range.from,
                to: range.from + 1,
              };
            }

            range.to = Math.min(
              editor.state.doc.nodeSize - 1,
              range.to + (fullQuery.length - fullQuery.lastIndexOf("@") - 1)
            );
          }

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .setMentionAt(range, props as unknown as RelatedItem)
            .run();

          window.getSelection()?.collapseToEnd();
        },
        items: () => [],
        render() {
          let component: ReactRenderer<ComponentRef<typeof MentionList>>;
          let popup: HTMLDivElement;
          let node: HTMLElement;

          const handleClickOutside = (event: MouseEvent) => {
            if (!popup.contains(event.target as HTMLElement)) {
              popup.remove();
            }
          };

          const handleClosePopup = () => {
            if (popup) {
              popup.remove();
            }
          };

          const handlePosition = () => {
            if (!node) return;

            computePosition(node, popup, {
              placement: "bottom-start",
              middleware: [offset(16), flip()],
            })
              .then((position) => {
                popup.style.top = `${position.y}px`;
                popup.style.left = `${position.x}px`;
              })
              .catch(console.error);
          };

          return {
            onStart: (props) => {
              const isMobile = window.innerWidth < DESKTOP_BREAKPOINT;

              if (isMobile) {
                props.editor.emit("openSpotlight");
                return;
              }

              component = new ReactRenderer(MentionList, {
                props: {
                  ...props,
                  onChange: (item: RelatedItem) => {
                    props.command(item);
                  },
                  onClose: () => {
                    popup.remove();
                  },
                },
                editor: props.editor,
              });

              popup = document.createElement("div");
              popup.style.zIndex = "9999";
              popup.style.position = "absolute";
              popup.style.width = "max-content";
              popup.style.top = "0";
              popup.style.left = "0";

              document.body.appendChild(popup);

              if (!props.clientRect) return;

              popup.appendChild(component.element);
              node = props.decorationNode as HTMLElement;

              window.addEventListener("click", handleClickOutside, true);
              window.addEventListener("mentions:updated", handlePosition);
              window.addEventListener("resize", handlePosition);
              window.addEventListener("mentions:close", handleClosePopup);

              handlePosition();
            },
            onUpdate: (props) => {
              component?.updateProps(props);

              if (!props.clientRect) return;

              node = props.decorationNode as HTMLElement;

              handlePosition();
            },
            onKeyDown(props) {
              if (props.event.key === "Escape") {
                popup.remove();
                return true;
              }

              return component?.ref?.onKeyDown(props) ?? false;
            },
            onExit: () => {
              popup?.remove();
              component?.destroy();
              window.removeEventListener("click", handleClickOutside, true);
              window.removeEventListener("mentions:updated", handlePosition);
              window.removeEventListener("resize", handlePosition);
              window.removeEventListener("mentions:close", handleClosePopup);
            },
          };
        },
      }),
    ];
  },
});
